import React, { createContext, FC, ReactNode, useContext } from 'react';

import { CURRENCIES } from '@savgroup-front-common/constants';
import { AdditionalInformationDto } from '@savgroup-front-common/types';

import { CommonWorkflowService } from '../../api';

export interface NeededInformation extends AdditionalInformationDto {
  internalId: string;
  fileUploadEndpoints?: Record<
    string,
    {
      uploadEndpoint: string;
      deleteEndpoint: string;
    }
  >;
}

interface AdditionalInformationContextProps {
  neededInformation: NeededInformation[];
  prefixFormName?: string;
  isLiveUpload: boolean;
  isNewDesign: boolean;
  fullSizeItem: boolean;
  isScrollIntoView: boolean;
  hasMultiProduct: boolean;
  suffixOptionButton?: React.ComponentType<any>;
  currency: CURRENCIES;
  claimId?: string;
  onAdditionalInformationSave: () => Promise<void>;
  onNeedToUploadFileAttachements?: (payload: { files: File[] }) => void;
  sellerId: string;
  fileReference?: string;
  getQrUploadLink:
    | typeof CommonWorkflowService.getFileAttachmentUploadLinkFromControlCommand
    | typeof CommonWorkflowService.getFileAttachmentUploadLinkFromMyAccountCommand;
}

const AdditionalInformationContext = createContext<
  AdditionalInformationContextProps | undefined
>(undefined);

interface AdditionalInformationProviderProps {
  children: ReactNode;
  neededInformation?: NeededInformation[];
  prefixFormName?: string;
  isLiveUpload?: boolean;
  isNewDesign?: boolean;
  fullSizeItem?: boolean;
  isScrollIntoView?: boolean;
  hasMultiProduct?: boolean;
  suffixOptionButton?: React.ComponentType<any>;
  currency?: CURRENCIES;
  claimId?: string;
  onAdditionalInformationSave?: () => Promise<void>;
  onNeedToUploadFileAttachements?: (payload: { files: File[] }) => void;
  sellerId: string;
  fileReference?: string;
  getQrUploadLink:
    | typeof CommonWorkflowService.getFileAttachmentUploadLinkFromControlCommand
    | typeof CommonWorkflowService.getFileAttachmentUploadLinkFromMyAccountCommand;
}

export const AdditionalInformationProvider: FC<
  AdditionalInformationProviderProps
> = ({
  children,
  neededInformation = [],
  prefixFormName,
  isLiveUpload = false,
  isNewDesign = false,
  fullSizeItem = false,
  isScrollIntoView = false,
  hasMultiProduct = false,
  suffixOptionButton,
  currency = CURRENCIES.EUR,
  claimId,
  onAdditionalInformationSave = () => Promise.resolve(),
  onNeedToUploadFileAttachements,
  sellerId,
  getQrUploadLink,
  fileReference,
}) => {
  const contextValue: AdditionalInformationContextProps = {
    neededInformation,
    prefixFormName,
    isLiveUpload,
    isNewDesign,
    fullSizeItem,
    isScrollIntoView,
    hasMultiProduct,
    suffixOptionButton,
    currency,
    claimId,
    onAdditionalInformationSave,
    onNeedToUploadFileAttachements,
    sellerId,
    getQrUploadLink,
    fileReference,
  };

  return (
    <AdditionalInformationContext.Provider value={contextValue}>
      {children}
    </AdditionalInformationContext.Provider>
  );
};

export const useAdditionalInformationContext =
  (): AdditionalInformationContextProps => {
    const context = useContext(AdditionalInformationContext);

    if (!context) {
      throw new Error(
        'useAdditionalInformationContext must be used within an AdditionalInformationProvider',
      );
    }

    return context;
  };
